import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { knowledgeApi, KnowledgeItem } from '../services/api';
import { ChevronDown, ChevronRight, Search, FileIcon, FolderIcon, BookOpen, Target, Instagram, Calendar, Users, Trello } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface KnowledgeSelectorProps {
  onSelect: (item: KnowledgeItem | null) => void;
  placeholder?: string;
  fileTypes?: string[];
  selectedItemId?: string | null;
  preloadedItems?: KnowledgeItem[];
}

// Мемоизированная версия компонента для уменьшения перерисовок
const KnowledgeSelectorComponent = memo(({ 
  onSelect, 
  placeholder = 'Выберите файл',
  fileTypes = [],
  selectedItemId = null,
  preloadedItems = undefined
}: KnowledgeSelectorProps) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<KnowledgeItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<KnowledgeItem | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number>(300);
  const selectorRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const loadedRef = useRef<boolean>(false);
  const fileTypesRef = useRef<string[]>(fileTypes);
  
  // Обновляем ref, если изменились fileTypes
  useEffect(() => {
    fileTypesRef.current = fileTypes;
  }, [fileTypes]);

  // Функция для определения оптимального позиционирования и прокрутки
  const calculateDropdownPosition = useCallback(() => {
    if (!selectorRef.current) return;

    const selectorRect = selectorRef.current.getBoundingClientRect();
    const DEFAULT_DROPDOWN_HEIGHT = 300; // Базовая желаемая высота
    const viewportHeight = window.innerHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Резерв под шапку и подвал модалки (sticky)
    const HEADER_SAFE_SPACE = 88; // px
    const FOOTER_SAFE_SPACE = 112; // px
    
    // Определяем, достаточно ли места снизу
    const spaceBelow = viewportHeight - selectorRect.bottom - FOOTER_SAFE_SPACE;
    const spaceAbove = selectorRect.top - HEADER_SAFE_SPACE;

    let shouldShowAbove = false;
    
    // Если места снизу недостаточно, но сверху больше
    if (spaceBelow < DEFAULT_DROPDOWN_HEIGHT && spaceAbove > spaceBelow) {
      shouldShowAbove = true;
    }

    setDropdownPosition(shouldShowAbove ? 'top' : 'bottom');

    // Прокручиваем страницу, если нужно
    const needsScroll = shouldShowAbove 
      ? spaceAbove < DEFAULT_DROPDOWN_HEIGHT 
      : spaceBelow < DEFAULT_DROPDOWN_HEIGHT;

    if (needsScroll) {
      const targetScrollTop = shouldShowAbove
        ? scrollTop - (DEFAULT_DROPDOWN_HEIGHT - spaceAbove) - 20 // Добавляем отступ
        : scrollTop + (DEFAULT_DROPDOWN_HEIGHT - spaceBelow) + 20; // Добавляем отступ

      // Плавная прокрутка
      window.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });
    }

    // Вычисляем максимально допустимую высоту так, чтобы список не перекрывал кнопки
    const available = shouldShowAbove ? Math.max(160, spaceAbove - 16) : Math.max(160, spaceBelow - 16);
    const maxH = Math.min(420, Math.max(180, available));
    setDropdownMaxHeight(maxH);
  }, []);

  // Функция для прокрутки к элементу, если он не виден
  const scrollIntoViewIfNeeded = useCallback(() => {
    if (!selectorRef.current) return;

    const selectorRect = selectorRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Проверяем, виден ли элемент
    const isVisible = selectorRect.top >= 0 && selectorRect.bottom <= viewportHeight;

    if (!isVisible) {
      selectorRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, []);
  
  // Функция для поиска элемента по ID в иерархии
  const findItemById = useCallback((items: KnowledgeItem[], targetId: string): KnowledgeItem | null => {
    for (const item of items) {
      if (item.id === targetId) {
        return item;
      }
      if (item.children) {
        const found = findItemById(item.children, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Функция для получения всех ID документов (для отладки)
  const getAllItemIds = useCallback((items: KnowledgeItem[]): string[] => {
    const ids: string[] = [];
    for (const item of items) {
      ids.push(item.id);
      if (item.children) {
        ids.push(...getAllItemIds(item.children));
      }
    }
    return ids;
  }, []);

  // Загрузка элементов при монтировании компонента и при открытии селектора
  const loadKnowledgeItems = useCallback(async () => {
    if (loadedRef.current) return;
    
    // Если есть предзагруженные элементы, используем их
    if (preloadedItems && preloadedItems.length > 0) {
      console.log('[KnowledgeSelector] Используем предзагруженные элементы:', preloadedItems.length);
      setItems(preloadedItems);
      loadedRef.current = true;
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('[KnowledgeSelector] Загрузка элементов базы знаний...');
      const currentFileTypes = fileTypesRef.current;
      console.log('[KnowledgeSelector] Фильтр по типам файлов:', currentFileTypes);
      
      const knowledgeItems = await knowledgeApi.getItems(
        currentFileTypes.length > 0 ? currentFileTypes : undefined
      );
      
      console.log('[KnowledgeSelector] Получено элементов:', knowledgeItems.length);
      if (knowledgeItems.length > 0) {
        console.log('[KnowledgeSelector] Пример элемента:', knowledgeItems[0]);
      }
      
      setItems(knowledgeItems);
      loadedRef.current = true;
    } catch (error) {
      console.error('[KnowledgeSelector] Ошибка при загрузке базы знаний:', error);
      setError('Не удалось загрузить элементы базы знаний');
    } finally {
      setIsLoading(false);
    }
  }, [preloadedItems]);

  // Эффект для обновления items при изменении preloadedItems
  useEffect(() => {
    if (preloadedItems && preloadedItems.length > 0) {
      console.log('[KnowledgeSelector] Обновляем items из preloadedItems:', preloadedItems.length);
      console.log('[KnowledgeSelector] fileTypes для фильтрации:', fileTypes);
      
      // Если есть фильтр по типам файлов, применяем его
      let filteredItems = preloadedItems;
      if (fileTypes.length > 0) {
        const filterByFileType = (items: KnowledgeItem[]): KnowledgeItem[] => {
          return items.reduce((acc: KnowledgeItem[], item) => {
            if (item.itemType === 'file' && fileTypes.includes(item.fileType || '')) {
              acc.push(item);
            } else if (item.itemType === 'folder' && item.children) {
              const filteredChildren = filterByFileType(item.children);
              if (filteredChildren.length > 0) {
                acc.push({
                  ...item,
                  children: filteredChildren
                });
              }
            }
            return acc;
          }, []);
        };
        
        filteredItems = filterByFileType(preloadedItems);
        console.log('[KnowledgeSelector] После фильтрации осталось элементов:', filteredItems.length);
      }
      
      setItems(filteredItems);
      loadedRef.current = true;
    }
  }, [preloadedItems, fileTypes]);

  // Эффект для установки предварительно выбранного элемента при изменении selectedItemId или items
  useEffect(() => {
    if (selectedItemId) {
      console.log('[KnowledgeSelector] Ищем документ с ID:', selectedItemId);
      console.log('[KnowledgeSelector] preloadedItems.length:', preloadedItems?.length || 0);
      console.log('[KnowledgeSelector] items.length:', items.length);
      
      // Сначала ищем в исходных preloadedItems (без фильтрации)
      let foundItem = null;
      if (preloadedItems && preloadedItems.length > 0) {
        foundItem = findItemById(preloadedItems, selectedItemId);
        console.log('[KnowledgeSelector] Поиск в preloadedItems:', foundItem ? `найден: ${foundItem.name}` : 'не найден');
      }
      // Если не нашли в preloadedItems, ищем в загруженных items
      if (!foundItem && items.length > 0) {
        foundItem = findItemById(items, selectedItemId);
        console.log('[KnowledgeSelector] Поиск в items:', foundItem ? `найден: ${foundItem.name}` : 'не найден');
      }
      
      if (foundItem) {
        console.log('[KnowledgeSelector] Устанавливаем предварительно выбранный элемент:', foundItem.name);
        setSelectedItem(foundItem);
      } else {
        console.log('[KnowledgeSelector] Предварительно выбранный элемент не найден:', selectedItemId);
        
        // Показываем все доступные ID для отладки
        if (preloadedItems && preloadedItems.length > 0) {
          const allIds = getAllItemIds(preloadedItems);
          console.log('[KnowledgeSelector] Доступные ID в preloadedItems:', allIds.slice(0, 10), allIds.length > 10 ? `... и еще ${allIds.length - 10}` : '');
        }
        if (items.length > 0) {
          const allIds = getAllItemIds(items);
          console.log('[KnowledgeSelector] Доступные ID в items:', allIds.slice(0, 10), allIds.length > 10 ? `... и еще ${allIds.length - 10}` : '');
        }
        
        console.log('[KnowledgeSelector] Возможные причины:');
        console.log('  - Документы еще не загружены');
        console.log('  - Документ удален или ID изменился');
        console.log('  - Документ не проходит фильтрацию по типам файлов');
        
        setSelectedItem(null);
      }
    } else {
      setSelectedItem(null);
    }
  }, [selectedItemId, items, preloadedItems, findItemById, getAllItemIds]);

  // Обработчик открытия/закрытия селектора
  const handleToggleOpen = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      
      // Небольшая задержка, чтобы дать React время отрендерить выпадающий список
      setTimeout(() => {
        scrollIntoViewIfNeeded();
        calculateDropdownPosition();
      }, 10);
      
      if (!loadedRef.current) {
        loadKnowledgeItems();
      }
    } else {
      setIsOpen(false);
    }
  }, [loadKnowledgeItems, isOpen, scrollIntoViewIfNeeded, calculateDropdownPosition]);

  // Обработчик клика вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // Добавляем обработчик, если селектор открыт
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Удаляем обработчик при размонтировании компонента или закрытии селектора
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Добавляем обработчик изменения размера окна для пересчета позиции
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      calculateDropdownPosition();
    };

    const handleScroll = () => {
      calculateDropdownPosition();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen, calculateDropdownPosition]);

  const toggleFolder = (folderId: string, event: React.MouseEvent) => {
    // Предотвращаем всплытие события, чтобы не закрыть селектор
    event.stopPropagation();
    
    console.log('[KnowledgeSelector] Переключение папки:', folderId);
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleItemSelect = (item: KnowledgeItem, event: React.MouseEvent) => {
    // Предотвращаем всплытие события
    event.stopPropagation();
    
    console.log('[KnowledgeSelector] Выбор элемента:', item.id, item.name);
    if (item.itemType === 'file') {
      setSelectedItem(item);
      onSelect(item);
      setIsOpen(false);
    }
  };

  // Функция для получения иконки файла (скопирована из KnowledgeFileStructure)
  const getFileIcon = (fileType?: string) => {
    switch (fileType) {
      case 'article':
      case 'summary':
        return <FileIcon className="w-4 h-4 mr-2 text-blue-500" strokeWidth={1.5} />;
      case 'notes':
        return <BookOpen className="w-4 h-4 mr-2 text-purple-500" strokeWidth={1.5} />;
      case 'roadmap':
        return <Target className="w-4 h-4 mr-2 text-orange-500" strokeWidth={1.5} />;
      case 'reels':
        return <Instagram className="w-4 h-4 mr-2 text-rose-500" strokeWidth={1.5} />;
      case 'content-plan':
        return <Calendar className="w-4 h-4 mr-2 text-teal-500" strokeWidth={1.5} />;
      case 'audience':
        return <Users className="w-4 h-4 mr-2 text-indigo-500" strokeWidth={1.5} />;
      case 'kanban':
        return <Trello className="w-4 h-4 mr-2 text-red-500" strokeWidth={1.5} />;
      default:
        return <FileIcon className="w-4 h-4 mr-2 text-gray-500" strokeWidth={1.5} />;
    }
  };

  const filteredItems = (items: KnowledgeItem[]): KnowledgeItem[] => {
    if (!items || items.length === 0) {
      console.log('[KnowledgeSelector] Нет элементов для фильтрации');
      return [];
    }
    
    if (!searchQuery) return items;
    
    const query = searchQuery.toLowerCase();
    console.log('[KnowledgeSelector] Фильтрация по запросу:', query);
    
    // Проверяем элементы
    return items.filter(item => {
      // Если это файл, проверяем соответствие названия и типа
      if (item.itemType === 'file') {
        const matchesFilter = fileTypes.length === 0 || 
          (item.fileType && fileTypes.includes(item.fileType));
        
        const matchesSearch = item.name.toLowerCase().includes(query);
        
        return matchesFilter && matchesSearch;
      }
      
      // Если это папка, проверяем рекурсивно её содержимое
      if (item.itemType === 'folder' && item.children && item.children.length > 0) {
        const filteredChildren = filteredItems(item.children);
        return filteredChildren.length > 0 || item.name.toLowerCase().includes(query);
      }
      
      return false;
    });
  };

  const renderItem = (item: KnowledgeItem, depth = 0) => {
    if (!item) return null;
    
    const isFolder = item.itemType === 'folder';
    const isExpanded = expandedFolders.has(item.id);

    // Для файлов проверяем, соответствует ли тип файла указанным ограничениям
    const isSelectableFile = item.itemType === 'file' && 
      (fileTypes.length === 0 || (item.fileType && fileTypes.includes(item.fileType)));

    return (
      <div key={item.id} className="w-full">
        <div 
          className={`flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md ${
            isSelectableFile || isFolder ? 'cursor-pointer' : 'cursor-default'
          } ${
            depth > 0 ? 'ml-' + (depth * 4) : ''
          } ${selectedItem?.id === item.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
          onClick={(e) => {
            if (isFolder) {
              toggleFolder(item.id, e);
            } else if (isSelectableFile) {
              handleItemSelect(item, e);
            }
          }}
        >
          {isFolder ? (
            <>
              <span className="mr-1 text-gray-600 dark:text-gray-400">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
              <FolderIcon className="w-4 h-4 mr-2 text-blue-500" strokeWidth={1.5} />
            </>
          ) : (
            getFileIcon(item.fileType)
          )}
          <span className={`truncate ${
            !isSelectableFile && item.itemType === 'file' 
              ? 'text-gray-400 dark:text-gray-500' 
              : 'text-gray-900 dark:text-gray-100'
          }`}>
            {item.name || 'Без имени'}
          </span>
        </div>
        
        {isFolder && isExpanded && item.children && item.children.length > 0 && (
          <div className="ml-2">
            {item.children.map(child => renderItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Определяем классы для позиционирования выпадающего списка
  const dropdownClasses = dropdownPosition === 'top' 
    ? 'absolute z-40 w-full bottom-full mb-1 bg-white dark:bg-gray-800 border-2 border-blue-200 dark:border-blue-800 rounded-xl shadow-xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden'
    : 'absolute z-40 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-blue-200 dark:border-blue-800 rounded-xl shadow-xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden';

  return (
    <div className="relative" ref={selectorRef}>
      <div
        className={`flex items-center justify-between p-3 border-2 transition-all duration-200 cursor-pointer text-gray-900 dark:text-gray-100 rounded-xl ${
          isOpen 
            ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-100 dark:ring-blue-800/30' 
            : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:border-blue-200 dark:hover:border-blue-700 hover:bg-blue-25 dark:hover:bg-blue-950/30'
        }`}
        onClick={handleToggleOpen}
      >
        <div className="flex-1 truncate">
          {selectedItem ? selectedItem.name : placeholder}
        </div>
        <div className="flex items-center gap-2">
          {selectedItem && (
            <button
              className="px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/40 rounded-md transition-all duration-200 font-medium"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/knowledge/${selectedItem.id}`);
              }}
              title="Открыть документ в базе знаний"
            >
              Открыть
            </button>
          )}
          <ChevronDown 
            size={18} 
            className={`transition-all duration-200 ${
              isOpen 
                ? 'text-blue-600 dark:text-blue-400 transform rotate-180' 
                : 'text-gray-500 dark:text-gray-400'
            }`} 
          />
        </div>
      </div>
      
      {isOpen && (
        <div className={dropdownClasses + ' flex flex-col'} ref={dropdownRef} style={{ maxHeight: dropdownMaxHeight }}>
          <div className="p-3 border-b border-blue-100 dark:border-blue-800">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск..."
                className="w-full p-2.5 pl-9 border border-blue-200 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/30 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 dark:text-blue-400" />
            </div>
          </div>
          
          <div className="overflow-y-auto p-2 flex-1">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">Загрузка...</div>
            ) : error ? (
              <div className="p-4 text-center text-red-500 dark:text-red-400">{error}</div>
            ) : items.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">Нет доступных файлов</div>
            ) : (
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Всего элементов: {items.length}</div>
                {filteredItems(items).map(item => renderItem(item))}
              </div>
            )}
          </div>
          
          <div className="p-3 border-t border-blue-100 dark:border-blue-800 flex justify-between bg-blue-50/30 dark:bg-blue-950/30">
            <button
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItem(null);
                onSelect(null);
                setIsOpen(false);
              }}
            >
              Очистить выбор
            </button>
            <button
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all duration-200 shadow-sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
            >
              Закрыть
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

// Сохраняем совместимость с текущими импортами
export const KnowledgeSelector = KnowledgeSelectorComponent; 